// SettingsPillsRow — переиспользуемый ряд model-specific pills.
//
// Назначение: рендер пилюль настроек (Формат, Разрешение, Длительность,
// Suno-controls, PresentationPills, HeyGen-pills и пр.), зависимых от
// текущей модели в GenerateContext. Используется:
//   1) /generate — внутри GenerateInputBar.tsx, заменяет inline pills блок.
//   2) Home (HomeTemp.tsx) — в bottom row chat-input-wrapper рядом со
//      скрепкой, чтобы юзер мог настроить генерацию прям с главной.
//
// Компонент self-contained: читает state из useGenerate() (App-level
// Provider), не принимает props. Это даёт shared state между home и
// /generate автоматически.
//
// Single source of truth: новые pill'ы добавляются ОДИН РАЗ здесь и
// автоматически появляются на обоих экранах. Никакого drift'а.
//
// Анимация входа pill'ов — через CSS-stagger по `--idx` (см.
// _generate-bar.scss / соседние стили). Каждая pill enter'ит с лёгким
// translateX(-12px) + scale(0.85) + fade-in от direction'а attach-кнопки
// (визуальный паттерн «pills выпрыгивают из скрепки»).
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGenerate } from '../../../context/GenerateContext'
import { useAttachedStyleCard } from '../../../api/attachedStyleCard'
import { isMotionCard } from '../motion/motionTool'
import type { GenerateSettings } from '../hooks/useGenerateSettings'
import { modelParams, outputFormatOptions, effectiveOutputFormat, inputsAttachedOf, trimMinSeconds } from '../utils/modelParams'
import {
	ASPECT_FOLLOWS_LABEL,
	NOTE_ASPECT_FOLLOWS,
	VIDEO_INTENT_HINTS,
	VIDEO_INTENT_OPTIONS,
	aspectFollowsReference,
	durationLock,
	motionDurationForOrientation,
	ownReferenceVideos,
	trendVideoNote,
	videoIntentOf,
	DRAFT_HINT,
	DRAFT_RESOLUTION_LABEL,
	NOTE_DRAFT,
} from '../utils/referenceLimits'
import { switchSeedanceMode as switchSeedanceModeShared } from '../utils/seedanceMode'
import { BbarSectionMode } from './bbarSectionContext'
import { switchVideoIntent } from '../utils/videoIntent'
import { getAudioFieldAvailability } from '../utils/audioOperationAvailability'
import { getHeyGenModelKind } from '../../../api/heygen'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { AspectRatioIcon } from '../../../components/AspectRatioIcon'

import BottomBarPopover from './BottomBarPopover'
import PresentationPills from './PresentationPills'
import SunoStyleButton from './SunoStyleButton'
import SunoPersonaPicker from './SunoPersonaPicker'
import TtsVoicePicker from './TtsVoicePicker'
import SoundtrackRangePill from './SoundtrackRangePill'
import PodcastSheet from './PodcastSheet'
import { TRACK_PARTS, trackPartLabel } from '../utils/trackParts'
import { providerOf } from '../utils/trackOperations'
import { qualityLabelFull } from '../utils/qualityLabels'
import {
	HeyGenAvatarIVPills,
	HeyGenVideoAgentPills,
	HeyGenTranslatePills,
} from './HeyGenPanels'

// AspectIcon вынесен в общий src/components/AspectRatioIcon.tsx (используется
// здесь, в ProductCardLab и др.). Импортируется как AspectRatioIcon.

// Дефолтные подписи tooltip'ов для пилюль. paramHints на model могут
// переопределить — admin кастомизация. Один словарь — одна точка правды
// для tooltip-текстов settings'ов.
const DEFAULT_HINTS: Record<string, string> = {
	draft: DRAFT_HINT,
	aspectRatio:
		'Соотношение сторон итогового медиафайла. 16:9 — горизонтальное, 9:16 — вертикальное (Reels/TikTok), 1:1 — квадрат.',
	duration: 'Длительность генерируемого видео в секундах.',
	resolution:
		'Качество и разрешение итогового файла. Более высокое разрешение увеличивает стоимость генерации.',
	sound: 'Автоматическая генерация звукового сопровождения: фоновые шумы, атмосфера и эффекты.',
	negativePrompt:
		'Что не должно появляться в результате. Перечислите нежелательные элементы через запятую.',
	promptOptimizer:
		'Модель автоматически улучшает ваш промт для получения более точного и качественного результата.',
	speed:
		'Скорость генерации. Turbo — быстрее и дешевле, Relaxed — медленнее, но экономичнее при высокой нагрузке.',
	stylization:
		'Насколько сильно модель применяет свой художественный стиль. 0 — близко к промту, 1000 — максимальная стилизация.',
	characterOrientation:
		'Ориентация аватара. «По изображению» — сохраняет позу (макс. 10 сек). «По видео» — повторяет ориентацию из видео (макс. 30 сек).',
	keepOriginalSound: 'Сохранить оригинальный звук из загруженного видео в итоговом результате.',
	googleSearch: 'Модель использует актуальную информацию из Google для более точной генерации.',
	webSearch: 'Использовать поиск в интернете для уточнения контекста.',
	nsfwChecker: 'Включить фильтрацию небезопасного контента.',
	generateAudio: 'Сгенерировать звуковое сопровождение синхронизированное с видео.',
	outputFormat:
		'Формат итогового изображения. JPG — меньше размер, PNG — без потерь, WebP — оптимальный баланс.',
	safetyFilter:
		'Уровень фильтрации контента. Минимальный — пропускает больше, Строгий — блокирует больше.',
	videoReferenceType:
		'«Стиль и камера» — перенос стиля съёмки. «Редактирование» — прямое редактирование видео.',
}

export default function SettingsPillsRow() {
	const { t } = useTranslation()
	const gen = useGenerate()
	// «Повторить движение»: звук всегда включён (владелец 19.10) — useMotionMode держит его, переключателей не рисуем.
	const motionTool = isMotionCard(useAttachedStyleCard())
	// Объявляем ДО раннего `if (!model) return null` — иначе хук стал бы условным.
	const [podcastOpen, setPodcastOpen] = useState(false)
	// В общей панели инструментов строка «Формат и длина» — секцией (заголовок + текст), в ряду пилюль — подписью.
	const asSection = useContext(BbarSectionMode)
	const {
		selectedModel: model,
		settings,
		// Пилюли меняют настройки только по действию человека — отмечаем их для блока «Тренд» в админке (11.10).
		userUpdateSetting: updateSetting,
		isLoading,
		isAudio,
		isPresentation,
		referenceVideo,
		referenceVideos,
		negativePromptOpen,
		setNegativePromptOpen,
		setUploadedImage,
		setEndImage,
		setReferenceImages,
		setReferenceVideos,
		setReferenceVideo,
		referenceAudios,
	} = gen

	const hasAudioRef = (referenceAudios?.length ?? 0) > 0

	// Без модели pills'ям нечего рендерить.
	if (!model) return null

	// Матрица доступности audio-полей (зависит от operation: extend/cover/...).
	// Берём напрямую из gen.audioOperation, тот же что в GenerateInputBar.
	const audioAvail = getAudioFieldAvailability(gen.audioOperation)

	// ЧТО показывать решает общий источник (utils/modelParams) — тот же, из
	// которого рендерит шторка телефона. Здесь остаётся только «как».
	const p = modelParams(model, {
		referenceVideoUrl: referenceVideo?.url,
		photoSlotsEngaged: gen.photoSlotsEngaged,
		seedanceMode: settings.seedanceMode,
		ownReferenceVideos: ownReferenceVideos(referenceVideos).length,
		inputsAttached: inputsAttachedOf(gen),
		draftAvailable: gen.draftAvailable,
	})
	const videoMin = trimMinSeconds(p)
	// Черновик Seedance 2.5 включён: разрешение у него одно (480p), пилюля это показывает и заперта.
	const draftOn = p.draft && settings.draft === true
	// Формат кадра при видео-референсе Seedance 2.5 задаёт референс (KIE принимает только
	// adaptive) — пилюля говорит об этом до запуска, а не молчит про выбранное 9:16.
	const aspectLocked = aspectFollowsReference(model, settings, referenceVideos ?? [])
	// Тренд переделывает своё видео (shared/trend-video): формат и длина — как у видео, выбирать нечего — вместо пилюль
	// формата и длительности одна строка «Формат и длина — как у видео тренда: 16:9, 15 с».
	const trendNote = trendVideoNote(model, settings, referenceVideos ?? [])
	const videoIntent = videoIntentOf(settings)
	// Списки значений: показывать их или нет решает спека выше, а тип у Model
	// опциональный — берём пустой массив, чтобы не сыпать «!» по разметке.
	const aspectRatios = model.aspectRatios ?? []
	const durations = model.durations ?? []
	const resolutions = model.resolutions ?? []
	const qualityLevels = model.qualityLevels ?? []
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const paramHints = (model as Record<string, any>).paramHints || {}
	const hint = (key: string): string | undefined =>
		paramHints[key]?.description || DEFAULT_HINTS[key]

	// При видео-референсе (video-to-video, напр. Gemini Omni) итоговую длину
	// выбирает модель — слайдер длительности НЕ отключаем (он задаёт цену и
	// целевой отрезок), но сообщаем об этом тултипом и заметкой.
	const videoRefActive =
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		!!referenceVideo?.url && !!(model as Record<string, any>).supportsReferenceVideo
	// Seedance 2.5 с одним видео-референсом: ролик выходит длиной референса — пилюля
	// показывает эту длину и заперта, цена считается от неё (utils/referenceLimits).
	// Тренд с замком длительности — та же запертая пилюля с длиной тренда (карточка тренда в админке, 11.10).
	const durLock = durationLock(model, settings, referenceVideos ?? [], !!gen.uploadedVideo?.url)
	const followSec = durLock?.sec ?? null
	const durationValue = followSec != null ? String(followSec) : settings.duration
	const durationHint = durLock
		? durLock.note
		: videoRefActive
			? 'При видео-референсе итоговую длину выбирает модель. Выбранная длительность задаёт стоимость и целевой отрезок исходного видео.'
			: hint('duration')

	// Опции для пилюль. Часть зависит от t() — переводы, поэтому inline.
	const speedOptions = [
		{ value: 'turbo', label: 'Turbo' },
		{ value: 'fast', label: 'Fast' },
		{ value: 'relaxed', label: 'Relaxed' },
	]
	const versionOptions = [
		{ value: '7', label: 'v7' },
		{ value: '6.1', label: 'v6.1' },
		{ value: '6', label: 'v6' },
		{ value: '5.2', label: 'v5.2' },
		{ value: 'niji6', label: 'Niji 6' },
	]
	const safetyOptions = [
		{ value: 'block_only_high', label: t('generate.settings.safety.minimal') },
		{ value: 'block_medium_and_above', label: t('generate.settings.safety.medium') },
		{ value: 'block_low_and_above', label: t('generate.settings.safety.strict') },
	]
	const formatOptions = outputFormatOptions(model)
	// Показываем ровно то, что уйдёт в запрос (buildImagePayload берёт тот же эффективный формат).
	const outputFormat = effectiveOutputFormat(model, settings.outputFormat)
	const orientationOptions = [
		{ value: 'video', label: t('generate.settings.orientation.byVideo') },
		{ value: 'image', label: t('generate.settings.orientation.byImage') },
	]
	const videoRefTypeOptions = [
		{ value: 'feature', label: t('generate.settings.videoRef.styleAndCamera') },
		{ value: 'base', label: t('generate.settings.videoRef.editing') },
	]

	// Presentation — рендерим её собственный pills-компонент (один pill,
	// открывает sheet с template/slideCount). Не оборачиваем в gm__pills чтобы
	// родитель сам решал layout (на home — bottom row, на /generate — bottom bar).
	if (isPresentation) {
		return <PresentationPills />
	}

	// HeyGen-модели: собственные наборы pills (avatar/agent/translate).
	// Если это HeyGen-модель, рендерим её pills и НИЧЕГО больше (другие
	// общие image/video pills не относятся).
	const heyKind = getHeyGenModelKind(model.backendModel)
	if (heyKind === 'avatar-iv') {
		return (
			<HeyGenAvatarIVPills settings={settings} updateSetting={updateSetting} disabled={isLoading} />
		)
	}
	if (heyKind === 'video-agent') {
		return (
			<HeyGenVideoAgentPills settings={settings} updateSetting={updateSetting} disabled={isLoading} />
		)
	}
	if (heyKind === 'video-translate') {
		return (
			<HeyGenTranslatePills settings={settings} updateSetting={updateSetting} disabled={isLoading} />
		)
	}

	// Audio sounds-режим: одна loop-пилюля.
	if (isAudio && settings.audioSubmode === 'sounds') {
		return (
			<BottomBarPopover
				tooltip="Генерировать как зацикленный SFX (для фонов: дождь, шум ветра)"
				icon={
					<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
						<circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
						{settings.soundLoop && <circle cx="8" cy="8" r="2.5" fill="#6ee7b7" />}
					</svg>
				}
				label="Loop"
				active={!!settings.soundLoop}
				onToggle={() => updateSetting('soundLoop', !settings.soundLoop)}
				disabled={isLoading}
			/>
		)
	}

	// Audio soundtrack-режим (Mureka, фото/видео→музыка): Mureka API принимает только
	// model + prompt. Стиль/персона/вокал/инструментал/кастом не применяются — не
	// показываем их, чтобы не вводить в заблуждение (модель — отдельный пикер слева,
	// подсказка-настроение — в текстовом поле).
	if (isAudio && settings.audioSubmode === 'tts') {
		return (
			<>
				<TtsVoicePicker
					currentVoice={settings.audioVoice}
					currentVoiceName={settings.audioVoiceName}
					onApply={(v) => {
						updateSetting('audioVoice', v.id)
						updateSetting('audioVoiceName', v.label)
					}}
					disabled={isLoading}
				/>
				{/* «Диалог» — реплики на несколько голосов (ElevenLabs text-to-dialogue).
				    Отдельным окном: у него не один текст, а список реплик, в поле
				    виджета это не влезает. Запуск живёт там же (приём как у
				    «Сгенерировать текст песни»). */}
				<BottomBarPopover
					tooltip="Диалог: реплики разными голосами в одном файле"
					sectionTitle="Диалог"
					icon={<Icon id="gen-audio" size={20} />}
					label="Диалог"
					active={podcastOpen}
					onToggle={() => setPodcastOpen(true)}
					disabled={isLoading}
				/>
				<PodcastSheet
					open={podcastOpen}
					onClose={() => setPodcastOpen(false)}
					model={model}
					onCreated={() => window.dispatchEvent(new CustomEvent('tts-generated'))}
				/>
			</>
		)
	}

	// Саундтрек: стиль/вокал/персона тут не применяются (Mureka принимает только
	// model + prompt + медиа), но у ВИДЕО можно выбрать кусок, под который писать
	// музыку. У фото длительности нет — пилюля не появляется.
	if (isAudio && settings.audioSubmode === 'soundtrack') {
		const dur = gen.uploadedVideo?.duration
		if (!dur) return null
		return (
			<SoundtrackRangePill
				duration={dur}
				startSec={settings.soundtrackStartSec}
				endSec={settings.soundtrackEndSec}
				onChange={(s, e) => {
					updateSetting('soundtrackStartSec', s)
					updateSetting('soundtrackEndSec', e)
				}}
				disabled={isLoading}
			/>
		)
	}

	// Audio music/lyrics-режим: набор Suno-pills (style, persona, vocal, instr, custom).
	if (isAudio) {
		return (
			<>
				<SunoStyleButton
					model={model}
					currentStyle={settings.audioStyle || ''}
					currentNegativeTags={settings.audioNegativeTags || ''}
					onApply={({ style, negativeTags }) => {
						updateSetting('audioStyle', style)
						updateSetting('audioNegativeTags', negativeTags)
					}}
					disabled={isLoading}
					audioOperation={gen.audioOperation}
				/>
				<span
					title={
						!audioAvail.persona.enabled
							? audioAvail.persona.reason
							: settings.audioInstrumental
								? 'Сейчас инструментал — выбор голоса выключит его'
								: undefined
					}
				>
					<SunoPersonaPicker
						currentPersonaId={settings.audioPersonaId}
						onApply={(personaId) => {
							updateSetting('audioPersonaId', personaId)
							// Голос и инструментал взаимоисключающи: выбор персоны —
							// явное «хочу вокал», инструментал гаснет сам.
							if (personaId && settings.audioInstrumental) updateSetting('audioInstrumental', false)
						}}
						disabled={isLoading || !audioAvail.persona.enabled}
						dimmed={!!settings.audioInstrumental}
						sourceTrackId={gen.audioOperation === 'extend' ? gen.sourceTrack?.id ?? null : null}
						modelId={model.id}
					/>
				</span>
				{/* «Партия» — что именно дописать к треку. Умеет только Mureka
				    (у Suno «+ Инструментал» добавляет музыку целиком), поэтому
				    пилюля появляется лишь для её треков и только в этой операции. */}
				{gen.audioOperation === 'add_instrumental'
					&& providerOf(gen.sourceTrack?.model) === 'mureka' && (
					<BottomBarPopover
						tooltip="Какую партию дописать к треку"
						sectionTitle="Партия"
						icon={<Icon id="gen-stems" size={20} />}
						label={trackPartLabel(settings.audioTrackType)}
						options={TRACK_PARTS.map((p) => ({ value: p.value, label: p.label }))}
						value={settings.audioTrackType || ''}
						onChange={(v) => updateSetting('audioTrackType', v)}
						disabled={isLoading}
					/>
				)}
				<BottomBarPopover
					tooltip={
						audioAvail.vocalGender.reason
							|| (settings.audioInstrumental
								? 'Сейчас инструментал — выбор вокала выключит его'
								: t('generate.audio.vocal'))
					}
					sectionTitle="Вокал"
					icon={<Icon id="gen-audio" size={20} />}
					label={
						settings.audioVocalGender === 'm'
							? t('generate.audio.vocalMale')
							: settings.audioVocalGender === 'f'
								? t('generate.audio.vocalFemale')
								: t('generate.audio.vocalAuto')
					}
					options={[
						{ value: '', label: t('generate.audio.vocalAuto') },
						{ value: 'm', label: t('generate.audio.vocalMale') },
						{ value: 'f', label: t('generate.audio.vocalFemale') },
					]}
					value={settings.audioVocalGender || ''}
					onChange={(v) => {
						updateSetting('audioVocalGender', v)
						// Последнее действие главнее: явный выбор вокала гасит инструментал.
						if (settings.audioInstrumental) updateSetting('audioInstrumental', false)
					}}
					dimmed={!!settings.audioInstrumental}
					disabled={isLoading || !audioAvail.vocalGender.enabled}
				/>
				<BottomBarPopover
					tooltip={audioAvail.instrumental.reason || t('generate.audio.instrumentalHint')}
					icon={
						<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
							<circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
							{settings.audioInstrumental && <circle cx="8" cy="8" r="2.5" fill="#6ee7b7" />}
						</svg>
					}
					label={t('generate.audio.instrumental')}
					active={settings.audioInstrumental}
					onToggle={() => updateSetting('audioInstrumental', !settings.audioInstrumental)}
					disabled={isLoading || !audioAvail.instrumental.enabled}
				/>
				<BottomBarPopover
					tooltip={audioAvail.customMode.reason || t('generate.audio.customHint')}
					icon={
						<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
							<circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
							{settings.audioCustomMode && <circle cx="8" cy="8" r="2.5" fill="#6ee7b7" />}
						</svg>
					}
					label={t('generate.audio.custom')}
					active={settings.audioCustomMode}
					onToggle={() => updateSetting('audioCustomMode', !settings.audioCustomMode)}
					disabled={isLoading || !audioAvail.customMode.enabled}
				/>
			</>
		)
	}

	// Image / Video pills (common image+video flow). Каждая пилюля
	// conditional по флагам/полям model — auto-driven, новые pill'ы
	// добавляются здесь и автоматом появляются и на /generate, и на home.
	// Логика переключения общая с телефоном (utils/seedanceMode): режим меняет
	// не только настройку, но и то, какие загрузки остаются осмысленными.
	const switchSeedanceMode = (mode: 'frames' | 'references' | 'video') =>
		switchSeedanceModeShared(
			{ settings, updateSetting, setUploadedImage, setEndImage, setReferenceImages, setReferenceVideos, setReferenceVideo },
			mode,
		)

	const seedanceModeLabel = (m: GenerateSettings['seedanceMode']) =>
		m === 'references' ? '🎨 Референсы' : '🖼 Кадры'

	return (
		<>
			{/* Пилюля режима спрятана при активных фото-слотах карточки: слоты
			    живут только в «Референсах» (переключение сломало бы их канал). */}
			{p.seedanceMode && (
				<BottomBarPopover
					tooltip={
						settings.seedanceMode === 'references'
							? 'Фото / видео / аудио референсы. Модель использует их одновременно.'
							: 'Загрузите начальный кадр. Последний кадр — необязателен.'
					}
					icon={<Icon id="gen-add" size={14} />}
					label={seedanceModeLabel(settings.seedanceMode ?? 'frames')}
					sectionTitle="Режим"
					options={[
						{ value: 'frames', label: '🖼 Кадры' },
						{ value: 'references', label: '🎨 Референсы' },
					]}
					value={settings.seedanceMode ?? 'frames'}
					onChange={(v) => switchSeedanceMode(v as 'frames' | 'references' | 'video')}
					disabled={isLoading}
				/>
			)}

			{/* Намерение при видео-референсе (Seedance 2.5): от него зависят длина ролика и
			    оплата — «Переделать видео» запирает длительность на референсе, «Новое по
			    референсу» отдаёт её юзеру. Логика одна с телефоном (utils/videoIntent). */}
			{p.videoIntent && (
				<BottomBarPopover
					tooltip={VIDEO_INTENT_HINTS[videoIntent]}
					sectionTitle="Что сделать с видео"
					icon={<Icon id="gen-video" size={14} />}
					label={VIDEO_INTENT_OPTIONS.find((o) => o.value === videoIntent)?.label ?? ''}
					options={VIDEO_INTENT_OPTIONS}
					value={videoIntent}
					onChange={(v) =>
						switchVideoIntent(
							{ model, settings, referenceVideos: referenceVideos ?? [], updateSetting },
							v === 'new' ? 'new' : 'edit',
						)
					}
					disabled={isLoading}
				/>
			)}

			{trendNote && (asSection ? (
				<div className="bbar-section">
					<div className="bbar-section__head">{trendNote.label}</div>
					<div className="bbar-section__text">{trendNote.value}</div>
				</div>
			) : (
				<span
					className="bbar-note"
					style={{ opacity: 0.7, fontSize: 12, alignSelf: 'center', maxWidth: 280, lineHeight: 1.2 }}
				>
					{trendNote.line}
				</span>
			))}

			{p.aspectRatio && !trendNote && (
				<BottomBarPopover
					tooltip={aspectLocked ? NOTE_ASPECT_FOLLOWS : hint('aspectRatio')}
					sectionTitle="Соотношение сторон"
					icon={<AspectRatioIcon ratio={settings.aspectRatio} />}
					label={
						aspectLocked
							? ASPECT_FOLLOWS_LABEL
							: settings.aspectRatio === 'match_input_image'
								? t('generate.settings.matchInputImage')
								: settings.aspectRatio
					}
					options={aspectRatios.map((r: string) => ({
						value: r,
						label: r === 'match_input_image' ? t('generate.settings.matchInputImage') : r,
						icon: <AspectRatioIcon ratio={r} />,
					}))}
					value={settings.aspectRatio}
					onChange={(v) => updateSetting('aspectRatio', v)}
					disabled={isLoading || aspectLocked}
				/>
			)}

			{p.aspectRatio && aspectLocked && !trendNote && (
				<span
					className="bbar-note"
					style={{ opacity: 0.7, fontSize: 12, alignSelf: 'center', maxWidth: 280, lineHeight: 1.2 }}
				>
					{NOTE_ASPECT_FOLLOWS}
				</span>
			)}

			{p.duration && !trendNote && (
				<BottomBarPopover
					tooltip={durationHint}
					sectionTitle="Длительность"
					icon={<Icon id="gen-duration" size={14} />}
					label={`${durationValue} ${t('generate.settings.sec')}`}
					options={durations.map((d: string) => ({
						value: d,
						label: `${d} ${t('generate.settings.sec')}`,
					}))}
					value={durationValue}
					onChange={(v) => {
						updateSetting('duration', v)
						if (model.provider === 'hailuo' && v === '10' && settings.resolution === '1080p')
							updateSetting('resolution', '768p')
					}}
					disabled={isLoading || followSec != null}
				/>
			)}

			{p.durationSlider && !trendNote && (
				<div className="bbar-slider-pill">
					<Icon id="gen-duration" size={14} />
					<span className="bbar-slider-pill__label">
						{durationValue} {t('generate.settings.sec')}
					</span>
					<input
						type="range"
						className="bbar-slider"
						min={Number(durations[0])}
						max={Number(durations[durations.length - 1])}
						value={Number(durationValue)}
						step={1}
						style={{
							background: (() => {
								const min = Number(durations[0])
								const max = Number(durations[durations.length - 1])
								const pct = ((Number(durationValue) - min) / (max - min)) * 100
								return `linear-gradient(to right, var(--bbar-slider-fill) ${pct}%, var(--bbar-slider-track) ${pct}%)`
							})(),
						}}
						onChange={(e) => {
							const v = String(e.target.value)
							updateSetting('duration', v)
							if (model.provider === 'hailuo' && Number(v) > 6 && settings.resolution === '1080p')
								updateSetting('resolution', '768p')
						}}
						disabled={isLoading || followSec != null}
					/>
					<span className="bbar-slider-pill__max">{durations[durations.length - 1]}</span>
				</div>
			)}

			{durLock && !trendNote && (
				<span
					className="bbar-note"
					style={{ opacity: 0.7, fontSize: 12, alignSelf: 'center', maxWidth: 280, lineHeight: 1.2 }}
				>
					{durLock.note}
				</span>
			)}

			{videoRefActive && (
				<span
					className="bbar-note"
					style={{ opacity: 0.7, fontSize: 12, alignSelf: 'center', maxWidth: 280, lineHeight: 1.2 }}
				>
					При видео-референсе итоговую длину выбирает модель; выбранная длительность влияет на стоимость.
				</span>
			)}

			{p.resolution && (
				<BottomBarPopover
					tooltip={hint('resolution')}
					sectionTitle="Разрешение"
					icon={<Icon id="gen-resolution" size={14} />}
					label={draftOn ? DRAFT_RESOLUTION_LABEL : settings.resolution}
					options={resolutions.map((r: string) => ({ value: r, label: r }))}
					value={draftOn ? DRAFT_RESOLUTION_LABEL : settings.resolution}
					onChange={(v) => {
						updateSetting('resolution', v)
						if (model.provider === 'hailuo' && v === '1080p' && settings.duration === '10')
							updateSetting('duration', '6')
					}}
					disabled={isLoading || draftOn}
				/>
			)}

			{p.sound && !motionTool && (
				<BottomBarPopover
					tooltip={hasAudioRef ? 'Звук обязателен при использовании аудио-референса' : hint('sound')}
					icon={
						settings.sound || hasAudioRef ? (
							<Icon id="gen-audio" size={20} />
						) : (
							<Icon id="gen-audio-off" size={20} color="#ef4444" />
						)
					}
					label="Audio"
					active={settings.sound || hasAudioRef}
					onToggle={() => !hasAudioRef && updateSetting('sound', !settings.sound)}
					disabled={isLoading || hasAudioRef}
				/>
			)}

			{p.negativePrompt && (
				<BottomBarPopover
					tooltip={hint('negativePrompt')}
					icon={<Icon id="gen-negative" size={14} />}
					label="Negative Prompt"
					active={negativePromptOpen}
					onToggle={() => setNegativePromptOpen(!negativePromptOpen)}
					disabled={isLoading}
				/>
			)}

			{p.promptOptimizer && (
				<BottomBarPopover
					tooltip={hint('promptOptimizer')}
					icon={
						<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
							<circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
							{settings.promptOptimizer ? <circle cx="8" cy="8" r="2.5" fill="#6ee7b7" /> : null}
						</svg>
					}
					label="Prompt Accuracy"
					active={settings.promptOptimizer}
					onToggle={() => updateSetting('promptOptimizer', !settings.promptOptimizer)}
					disabled={isLoading}
				/>
			)}

			{p.generateAudio && !motionTool && (
				<BottomBarPopover
					tooltip={hasAudioRef ? 'Звук обязателен при использовании аудио-референса' : hint('generateAudio')}
					icon={
						settings.sound || hasAudioRef ? (
							<Icon id="gen-audio" size={20} />
						) : (
							<Icon id="gen-audio-off" size={20} color="#ef4444" />
						)
					}
					label="Звук"
					active={settings.sound || hasAudioRef}
					onToggle={() => !hasAudioRef && updateSetting('sound', !settings.sound)}
					disabled={isLoading || hasAudioRef}
				/>
			)}

			{p.draft && (
				<BottomBarPopover
					tooltip={hint('draft')}
					icon={<Icon id="gen-speed" size={14} />}
					label="Черновик"
					active={settings.draft === true}
					onToggle={() => updateSetting('draft', !settings.draft)}
					disabled={isLoading}
				/>
			)}

			{draftOn && (
				<span
					className="bbar-note"
					style={{ opacity: 0.7, fontSize: 12, alignSelf: 'center', maxWidth: 280, lineHeight: 1.2 }}
				>
					{NOTE_DRAFT}
				</span>
			)}

			{p.webSearch && (
				<BottomBarPopover
					tooltip={hint('webSearch')}
					icon={<Icon id="gen-search" size={14} />}
					label="Web"
					active={!!settings.webSearch}
					onToggle={() => updateSetting('webSearch', !settings.webSearch)}
					disabled={isLoading}
				/>
			)}

			{/* NSFW-пилюля намеренно скрыта через `false &&` — это не баг, оставляем как было */}
			{/* eslint-disable-next-line no-constant-binary-expression */}
			{false && model?.supportsNsfwChecker && (
				<BottomBarPopover
					tooltip={hint('nsfwChecker')}
					icon={
						<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
							<circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
							{settings.nsfwChecker !== false ? (
								<circle cx="8" cy="8" r="2.5" fill="#6ee7b7" />
							) : null}
						</svg>
					}
					label="NSFW"
					active={settings.nsfwChecker !== false}
					onToggle={() => updateSetting('nsfwChecker', !(settings.nsfwChecker !== false))}
					disabled={isLoading}
				/>
			)}

			{p.midjourney && (
				<BottomBarPopover
					tooltip={hint('speed')}
					sectionTitle="Скорость"
					icon={<Icon id="gen-speed" size={14} />}
					label={speedOptions.find((o) => o.value === settings.speed)?.label ?? settings.speed}
					options={speedOptions}
					value={settings.speed}
					onChange={(v) => updateSetting('speed', v as GenerateSettings['speed'])}
					disabled={isLoading}
				/>
			)}

			{p.midjourney && (
				<BottomBarPopover
					icon={<Icon id="gen-version" size={14} />}
					sectionTitle="Версия"
					label={`v${settings.version}`}
					options={versionOptions}
					value={settings.version}
					onChange={(v) => updateSetting('version', v)}
					disabled={isLoading}
				/>
			)}

			{p.midjourney && (
				<BottomBarPopover
					tooltip={hint('stylization')}
					sectionTitle="Стилизация"
					icon={<Icon id="gen-stylization" size={14} />}
					label={`Стиль ${settings.stylization}`}
					slider={{
						min: 0,
						max: 1000,
						step: 10,
						value: settings.stylization,
						onChange: (v) => updateSetting('stylization', v),
					}}
					disabled={isLoading}
				/>
			)}

			{p.imagen && (
				<BottomBarPopover
					tooltip={hint('safetyFilter')}
					sectionTitle="Фильтр контента"
					icon={<Icon id="gen-safety" size={14} />}
					label={safetyOptions.find((o) => o.value === settings.safetyFilter)?.label ?? 'Фильтр'}
					options={safetyOptions}
					value={settings.safetyFilter}
					onChange={(v) => updateSetting('safetyFilter', v as GenerateSettings['safetyFilter'])}
					disabled={isLoading}
				/>
			)}

			{p.outputFormat && (
				<BottomBarPopover
					tooltip={hint('outputFormat')}
					sectionTitle="Формат файла"
					icon={<Icon id="gen-image" size={14} />}
					label={formatOptions.find((o) => o.value === outputFormat)?.label ?? outputFormat.toUpperCase()}
					options={formatOptions}
					value={outputFormat}
					onChange={(v) => updateSetting('outputFormat', v as GenerateSettings['outputFormat'])}
					disabled={isLoading}
				/>
			)}

			{p.quality && (
				<BottomBarPopover
					tooltip="Качество генерации"
					sectionTitle="Качество"
					icon={<Icon id="gen-image" size={14} />}
					label={
						settings.quality === 'low'
							? 'Быстрое'
							: settings.quality === 'high'
								? 'Высокое'
								: 'Среднее'
					}
					options={qualityLevels.map((q: string) => ({
						value: q,
						label:
							qualityLabelFull(q),
					}))}
					value={settings.quality}
					onChange={(v) => updateSetting('quality', v)}
					disabled={isLoading}
				/>
			)}

			{p.motionControl && (
				<BottomBarPopover
					tooltip={hint('characterOrientation')}
					sectionTitle="Ориентация аватара"
					icon={
						settings.characterOrientation === 'video' ? (
							<Icon id="gen-video" size={20} />
						) : (
							<Icon id="gen-image" size={14} />
						)
					}
					label={orientationOptions.find((o) => o.value === settings.characterOrientation)?.label ?? ''}
					options={orientationOptions.map((o) => ({
						...o,
						icon:
							o.value === 'video' ? (
								<Icon id="gen-video" size={20} />
							) : (
								<Icon id="gen-image" size={14} />
							),
					}))}
					value={settings.characterOrientation}
					onChange={(v) => {
						updateSetting('characterOrientation', v as GenerateSettings['characterOrientation'])
						// «По изображению» Kling берёт до 10 с — длиннее отказывал через минуты ожидания.
						const capped = motionDurationForOrientation(v, gen.selectedVideoDuration)
						if (capped !== gen.selectedVideoDuration) gen.setSelectedVideoDuration(capped)
					}}
					disabled={isLoading}
				/>
			)}

			{(p.motionControl || p.videoReplace) && !motionTool && (
				<BottomBarPopover
					tooltip={hint('keepOriginalSound')}
					icon={
						settings.keepOriginalSound ? (
							<Icon id="gen-audio" size={20} />
						) : (
							<Icon id="gen-audio-off" size={20} color="#ef4444" />
						)
					}
					label={t('generate.settings.soundVideo')}
					active={settings.keepOriginalSound}
					onToggle={() => updateSetting('keepOriginalSound', !settings.keepOriginalSound)}
					disabled={isLoading}
				/>
			)}

			{/* «Повторить движение»: длину задают ручки отрезка в зоне ролика — второй ползунок здесь разошёлся бы с ними. */}
			{(p.motionControl || p.videoReplace) && !motionTool && gen.uploadedVideo?.duration && gen.uploadedVideo.duration > videoMin && (
				<div className="bbar-slider-pill">
					<Icon id="gen-duration" size={14} />
					<span className="bbar-slider-pill__label">
						{gen.selectedVideoDuration} {t('generate.settings.sec')}
					</span>
					<input
						type="range"
						className="bbar-slider"
						min={videoMin}
						max={gen.uploadedVideo.duration}
						value={gen.selectedVideoDuration}
						step={1}
						style={{
							background: (() => {
								const min = videoMin
								const max = gen.uploadedVideo!.duration!
								const pct = ((gen.selectedVideoDuration - min) / (max - min)) * 100
								return `linear-gradient(to right, var(--bbar-slider-fill) ${pct}%, var(--bbar-slider-track) ${pct}%)`
							})(),
						}}
						title="Длительность равна длине видео-референса (до 30 сек). Ползунок обрезает референс."
						onChange={(e) => {
							const v = Number(e.target.value)
							gen.setSelectedVideoDuration(v)
							if (p.motionControl && v > 10 && settings.characterOrientation === 'image')
								updateSetting('characterOrientation', 'video')
						}}
						disabled={isLoading}
					/>
					<span className="bbar-slider-pill__max">{gen.uploadedVideo.duration}</span>
				</div>
			)}

			{referenceVideo?.url && (
				<BottomBarPopover
					tooltip={hint('videoReferenceType')}
					sectionTitle="Видео-референс"
					icon={<Icon id="gen-version" size={14} />}
					label={
						videoRefTypeOptions.find((o) => o.value === settings.videoReferenceType)?.label ?? ''
					}
					options={videoRefTypeOptions}
					value={settings.videoReferenceType}
					onChange={(v) =>
						updateSetting('videoReferenceType', v as GenerateSettings['videoReferenceType'])
					}
					disabled={isLoading}
				/>
			)}

			{p.googleSearch && (
				<BottomBarPopover
					tooltip={hint('googleSearch')}
					icon={
						<svg width="14" height="14" viewBox="0 0 16 16" fill="none">
							<circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
							{settings.googleSearch && <circle cx="8" cy="8" r="2.5" fill="#6ee7b7" />}
						</svg>
					}
					label="Google Search"
					active={settings.googleSearch}
					onToggle={() => updateSetting('googleSearch', !settings.googleSearch)}
					disabled={isLoading}
				/>
			)}
		</>
	)
}
